import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetDepartments } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import { IDepartment } from '../../../@types/model/masterData/department/department';
import DepartmentHttpService from '../../../services/http/masterData/departmentHttpService';
import { IDepartmentFormValues } from '../../../@types/model/masterData/department/departmentFormValues';
import DepartmentForm from './form/DepartmentForm';
import { ConfirmationDialog, CustomTable, ProjectDialog } from '@zz2/zz2-ui';
import { IApiError } from '../../../@types/other';
import { setDepartmentMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import { useLocation } from 'react-router-dom';
import UserHelper from '../../../services/helper/userHelper';
import { RightGrantsEnum } from '../../../@types/enum/rightGrantsEnum';
import DepartmentModelHelper from '../../../@types/model/masterData/department/departmentModelHelper';

const DepartmentScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const departments = useAppSelector(state => state.masterData.departments);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedDepartment, setSelectedDepartment] = useState<IDepartment | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [deletingDepartment, setDeletingDepartment] = useState<IDepartment | null>(null);

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        fetchData();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const fetchData = async () => {
        const isIndexedDBAvailable = !!self.indexedDB;
        if (isIndexedDBAvailable) {
            await syncMasterData(false);
        } else {
            setIsLoading(true);
            try {
                const res = await DepartmentHttpService.getDepartmentData();
                dispatch(dataSetDepartments(res.data));
            } catch (error : unknown) {
                const apiError = error as IApiError | undefined;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading departments.');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const onSubmit = async (values : IDepartmentFormValues) => {
        setIsLoading(true);

        try {
            const upsert = DepartmentModelHelper.createUpsert(values);
            const res = await DepartmentHttpService.addOrUpdateDepartment(upsert);
            const updatedList = upsertArrayElement(departments, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetDepartments(updatedList));
            await setDepartmentMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedDepartment ? 'Department updated successfully.' : 'Department added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating department data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeDepartment = async () => {
        if (!deletingDepartment) return;
        const updatedDepartment = { ...deletingDepartment, isActive: false };
        setIsLoading(true);
        try {
            const res = await DepartmentHttpService.deleteDepartment(updatedDepartment.id);
            const updatedList = upsertArrayElement(departments, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetDepartments(updatedList));
            await setDepartmentMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Department deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting department.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedDepartment(null);
    };

    const edit = (selectedRow : IDepartment) => {
        setSelectedDepartment(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : IDepartment) : void => {
        setDeletingDepartment(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingDepartment(null);
    };

    const editButtonTooltip = (row : IDepartment) : string => {
        let message = 'Edit';

        if (!userHasRights.editRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Department', RightGrantsEnum.Edit);
        } else if (row.isImported) {
            message = 'Cannot edit imported entries.';
        }

        return message;
    };

    const deleteButtonTooltip = (row : IDepartment) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Department', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        } else if (row.isImported) {
            message = 'Cannot delete imported entries.';
        }

        return message;
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const initialValues = useMemo<IDepartmentFormValues>(() => {
        return DepartmentModelHelper.createFormValues(selectedDepartment);
    }, [selectedDepartment, isDialogOpen]);

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading}>
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<IDepartment>
                        
                        enableAdding
                        // addTooltipRightValidation={!userHasRights.addRight}
                        disableAddButton={!userHasRights.addRight || isLoading}
                        addTooltip={!userHasRights.addRight ? UserHelper.getRelatedRightToolTipMessage('Department', RightGrantsEnum.Add) : 'Add'}
                        addFunction={openModal}
                        // addTooltipOnClick={() : void => requestAccess('Add')}
                    
                        enableEditing
                        // editTooltipRightValidation={!userHasRights.editRight}
                        disableEditButton={(row : IDepartment) => !userHasRights.editRight || row.isImported || isLoading}
                        editTooltip={editButtonTooltip}
                        editFunction={edit}
                        // editTooltipOnClick={() : void => requestAccess('Edit')}

                        enableDeleting
                        // deleteTooltipRightValidation={!userHasRights.deleteRight}
                        disableDeleteButton={(row : IDepartment) : boolean => !userHasRights.deleteRight || !row.isActive || row.isImported || isLoading}
                        deleteTooltip={deleteButtonTooltip}
                        deleteFunction={onDelete}
                        // deleteTooltipOnClick={() : void => requestAccess('Delete')}
                    
                        enableSorting
                        enableFiltering
                        enablePagination
                        fitWidthToPage
                        columns={[
                            { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                            { title: 'Short Description', field: 'shortDescription', enableFiltering: true, enableSorting: true },
                            { title: 'Description', field: 'description', enableFiltering: true, enableSorting: true },
                            { title: 'Imported?', field: 'isImported', type: 'boolean', enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        rows={departments}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            {isDialogOpen &&
                <ProjectDialog
                    title={selectedDepartment ? 'Edit Department' : 'Add Department'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'md'}
                >
                    <DepartmentForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                        isImported={selectedDepartment?.isImported ?? false}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Department'}
                open={!!deletingDepartment}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeDepartment}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default DepartmentScreen;
